import { BugReportIcon, LightbulbIcon, PsychologyIcon, RefreshIcon, SearchIcon } from '../theme/icons';
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Alert,
  Select, MenuItem, FormControl, InputLabel, TextField, InputAdornment, IconButton, Tooltip, CircularProgress, Grid, Button,
} from '@mui/material';
import LoadingSpinner from './LoadingSpinner';
import { AzureCredentials } from '../types';
import { getVulnerabilityReport, generateSingleRemediationSuggestion } from '../services/api';
import { VulnerabilityReport } from '../types';

interface VulnerabilityManagementProps { credentials: AzureCredentials; }

const sevColor = (s: string) => s === 'Critical' ? 'error' : s === 'High' ? 'warning' : s === 'Medium' ? 'info' : 'default';

const cvssBar = (score: number) => {
  const pct = Math.min(score / 10 * 100, 100);
  const color = score >= 9 ? '#d13438' : score >= 7 ? '#e67e00' : score >= 4 ? '#0078d4' : '#107c10';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, bgcolor: '#f0f0f0', borderRadius: 1, height: 6, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, bgcolor: color, height: '100%', borderRadius: 1 }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 28 }}>{score.toFixed(1)}</Typography>
    </Box>
  );
};

const VulnerabilityManagement: React.FC<VulnerabilityManagementProps> = ({ credentials }) => {
  const [report, setReport] = useState<VulnerabilityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [severity, setSeverity] = useState('All');
  const [search, setSearch] = useState('');
  const [vulnAiInsights, setVulnAiInsights] = useState<Record<number, any>>({});
  const [loadingVulnAi, setLoadingVulnAi] = useState<number | null>(null);
  const [aiVulnOpen, setAiVulnOpen] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try { setReport(await getVulnerabilityReport(credentials)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (credentials.sessionId) load();
  }, [credentials.sessionId, credentials.subscriptionIds?.join(',')]);

  const handleVulnAiAnalysis = async (vuln: any, index: number) => {
    // If already loaded, toggle display
    if (vulnAiInsights[index]) {
      setAiVulnOpen(aiVulnOpen === index ? null : index);
      return;
    }

    setLoadingVulnAi(index);
    try {
      const response = await generateSingleRemediationSuggestion(
        credentials,
        vuln.cveId || `VULN-${index}`,
        vuln.title,
        vuln.title,
        vuln.severity,
        vuln.resourceName,
        vuln.resourceType,
        'Security'
      );

      if (response.success && response.suggestion) {
        setVulnAiInsights(prev => ({ ...prev, [index]: response.suggestion }));
        setAiVulnOpen(index);
      }
    } catch (error) {
      console.error('Error loading AI analysis for vulnerability:', error);
    } finally {
      setLoadingVulnAi(null);
    }
  };

  if (loading && !report) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const filtered = (report?.vulnerabilities ?? []).filter(v => {
    const matchSev = severity === 'All' || v.severity === severity;
    const q = search.toLowerCase();
    const matchSearch = !search || v.title.toLowerCase().includes(q) || v.cveId.toLowerCase().includes(q) || v.resourceName.toLowerCase().includes(q);
    return matchSev && matchSearch;
  });

  return (
    <Box>
      {report && (
        <>
          {report.critical > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {report.critical} critical vulnerabilities require immediate attention (CC7.2).
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {[
              { label: 'Critical', value: report.critical, color: '#d13438' },
              { label: 'High', value: report.high, color: '#e67e00' },
              { label: 'Medium', value: report.medium, color: '#0078d4' },
              { label: 'Low', value: report.low, color: '#107c10' },
              { label: 'Total Unremediated', value: report.totalUnremediated, color: '#5c2d91' },
            ].map(kpi => (
              <Card key={kpi.label} sx={{ flex: 1 }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  <BugReportIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Vulnerability Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Severity</InputLabel>
                    <Select value={severity} label="Severity" onChange={e => setSeverity(e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      {['Critical', 'High', 'Medium', 'Low'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" placeholder="Search CVE, resource..."
                    value={search} onChange={e => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  />
                  <Button variant="outlined" size="small"
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={load} disabled={loading}
                  >
                    {loading ? 'Loading…' : 'Refresh'}
                  </Button>
                </Box>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>CVE</strong></TableCell>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Resource</strong></TableCell>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell><strong>CVSS</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>AI Analysis</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.slice(0, 200).map((v, i) => (
                      <React.Fragment key={i}>
                        <TableRow hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            {v.cveId || '—'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {v.title}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {v.resourceName}
                          </TableCell>
                          <TableCell>
                            <Chip label={v.severity} size="small" color={sevColor(v.severity) as any} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            {v.cvssScore > 0 ? cvssBar(v.cvssScore) : <Typography variant="caption" color="text.secondary">N/A</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{v.category || '—'}</TableCell>
                          <TableCell>
                            <Chip label={v.status || 'Open'} size="small" color={v.status === 'Healthy' ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={vulnAiInsights[i] ? "View AI Analysis" : "Get AI Analysis"}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleVulnAiAnalysis(v, i)}
                                color={vulnAiInsights[i] ? "primary" : "default"}
                              >
                                {loadingVulnAi === i ? (
                                  <CircularProgress size={16} />
                                ) : vulnAiInsights[i] ? (
                                  <LightbulbIcon fontSize="small" />
                                ) : (
                                  <PsychologyIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Expandable AI Analysis Row */}
                        {aiVulnOpen === i && vulnAiInsights[i] && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ bgcolor: '#f8f9fa', borderLeft: '4px solid #0066CC', p: 0 }}>
                              <Box sx={{ p: 2 }}>
                                {/* Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <LightbulbIcon sx={{ color: '#0066CC', fontSize: 28 }} />
                                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                                    {vulnAiInsights[i].title}
                                  </Typography>
                                  <Chip 
                                    label={vulnAiInsights[i].automation} 
                                    size="small"
                                    color={
                                      vulnAiInsights[i].automation === 'Automated' ? 'success' :
                                      vulnAiInsights[i].automation === 'SemiAutomated' ? 'warning' : 'default'
                                    }
                                  />
                                  <Chip label={`Effort: ${vulnAiInsights[i].effort}`} size="small" variant="outlined" />
                                  <Chip label={`Time: ${vulnAiInsights[i].timeEstimate}`} size="small" variant="outlined" />
                                </Box>

                                <Grid container spacing={2}>
                                  {/* Description */}
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      {vulnAiInsights[i].description}
                                    </Typography>
                                  </Grid>

                                  {/* Root Cause */}
                                  {vulnAiInsights[i].rootCause && (
                                    <Grid item xs={12}>
                                      <Alert severity="info" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Root Cause</Typography>
                                        <Typography variant="body2">{vulnAiInsights[i].rootCause}</Typography>
                                      </Alert>
                                    </Grid>
                                  )}

                                  {/* Remediation Steps */}
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Remediation Steps</Typography>
                                    <Box component="ol" sx={{ pl: 2, m: 0 }}>
                                      {vulnAiInsights[i].remediationSteps?.map((step: string, idx: number) => (
                                        <li key={idx}>
                                          <Typography variant="body2" sx={{ mb: 0.5 }}>{step}</Typography>
                                        </li>
                                      ))}
                                    </Box>
                                  </Grid>

                                  {/* Quick Fix Commands */}
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Quick Fix Commands</Typography>
                                    
                                    {/* Azure CLI */}
                                    {vulnAiInsights[i].azureCliCommands && vulnAiInsights[i].azureCliCommands.length > 0 && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#0078D4' }}>Azure CLI:</Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: '#1e1e1e', mt: 0.5 }}>
                                          {vulnAiInsights[i].azureCliCommands.slice(0, 3).map((cmd: string, idx: number) => (
                                            <Typography
                                              key={idx}
                                              variant="caption"
                                              sx={{
                                                fontFamily: 'monospace',
                                                color: '#d4d4d4',
                                                display: 'block',
                                                mb: idx < 2 ? 0.5 : 0,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all'
                                              }}
                                            >
                                              {cmd}
                                            </Typography>
                                          ))}
                                        </Paper>
                                      </Box>
                                    )}

                                    {/* PowerShell */}
                                    {vulnAiInsights[i].powerShellCommands && vulnAiInsights[i].powerShellCommands.length > 0 && (
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#0078D4' }}>PowerShell:</Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: '#012456', mt: 0.5 }}>
                                          {vulnAiInsights[i].powerShellCommands.slice(0, 3).map((cmd: string, idx: number) => (
                                            <Typography
                                              key={idx}
                                              variant="caption"
                                              sx={{
                                                fontFamily: 'monospace',
                                                color: '#ffffff',
                                                display: 'block',
                                                mb: idx < 2 ? 0.5 : 0,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all'
                                              }}
                                            >
                                              {cmd}
                                            </Typography>
                                          ))}
                                        </Paper>
                                      </Box>
                                    )}
                                  </Grid>

                                  {/* Compliance Impact */}
                                  {vulnAiInsights[i].complianceImpact && (
                                    <Grid item xs={12}>
                                      <Alert severity="warning">
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Security Impact</Typography>
                                        <Typography variant="body2">{vulnAiInsights[i].complianceImpact}</Typography>
                                      </Alert>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          {report.vulnerabilities.length === 0 ? 'No vulnerabilities detected. Great security posture!' : 'No results match your filters.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filtered.length > 200 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing 200 of {filtered.length} vulnerabilities
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}
      {!loading && !report && <Alert severity="info">Select a subscription to view vulnerability data.</Alert>}
    </Box>
  );
};

export default VulnerabilityManagement;
